import { useState } from "react";
import { Heart, MessageCircle, Repeat2 } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * One masonry tile: image OR video, with creator chip + counts.
 * Click anywhere on the tile (except creator chip) opens the detail modal.
 */
export const CommunityCard = ({ post, onOpen, onLike }) => {
  const [hovered, setHovered] = useState(false);
  const isVideo = post.kind === "video";

  return (
    <button
      type="button"
      data-testid={`community-card-${post.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen(post)}
      className={`group block w-full text-left rounded-3xl overflow-hidden bg-white border transition-all duration-300 shadow-sm hover:shadow-ambient mb-4 break-inside-avoid ${
        post.is_top_weekend
          ? "border-amber-400 ring-2 ring-amber-300/40"
          : "border-ink-200/70 hover:border-ink-950"
      }`}
    >
      <div className="relative w-full bg-ink-100 overflow-hidden">
        {post.is_top_weekend && post.top_rank && (
          <span
            data-testid={`community-card-rank-${post.id}`}
            className={`absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-xs font-black shadow-lg ring-2 ring-white/60 ${
              post.top_rank === 1
                ? "bg-gradient-to-r from-yellow-300 to-amber-500 text-ink-950"
                : post.top_rank === 2
                ? "bg-gradient-to-r from-slate-200 to-slate-400 text-ink-950"
                : post.top_rank === 3
                ? "bg-gradient-to-r from-orange-300 to-amber-700 text-white"
                : "bg-gradient-to-r from-amber-400 to-coral-500 text-white"
            }`}
          >
            #{post.top_rank}
          </span>
        )}
        {!post.is_top_weekend && post.is_new_creator && (
          <span
            data-testid={`community-card-new-${post.id}`}
            className="absolute top-3 left-3 z-10 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-black tracking-[0.18em] shadow-md"
          >
            FRESH
          </span>
        )}
        {isVideo ? (
          <video
            src={post.media_url}
            muted
            loop
            playsInline
            autoPlay={hovered}
            preload="metadata"
            className="w-full h-auto block object-cover"
            data-testid={`community-card-video-${post.id}`}
            onError={(e) => {
              // Reel file was lost during a redeploy (ephemeral /api/uploads/* refs).
              // Hide the whole tile so the feed doesn't look broken.
              const tile = e.currentTarget.closest("button");
              if (tile) tile.style.display = "none";
            }}
          />
        ) : (
          <img
            src={post.media_url}
            alt=""
            loading="lazy"
            className="w-full h-auto block object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            onError={(e) => {
              const tile = e.currentTarget.closest("button");
              if (tile) tile.style.display = "none";
            }}
          />
        )}

        {/* Bottom gradient + actions */}
        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-ink-950/85 via-ink-950/40 to-transparent flex items-end justify-between gap-2">
          <Link
            to={`/dashboard/community/u/${post.user_id}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`community-card-creator-${post.id}`}
            className="inline-flex items-center gap-2 text-white text-xs font-semibold hover:underline"
          >
            <span className="w-6 h-6 rounded-full bg-coral-500 text-white flex items-center justify-center text-[10px] font-black">
              {post.user_initial}
            </span>
            <span className="truncate max-w-[100px]">{post.user_name}</span>
          </Link>
          <div className="flex items-center gap-2">
            <span
              role="button"
              tabIndex={0}
              data-testid={`community-card-like-${post.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onLike(post);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onLike(post);
                }
              }}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold backdrop-blur-md transition-all cursor-pointer ${
                post.liked_by_me
                  ? "bg-coral-500 text-white"
                  : "bg-white/15 text-white hover:bg-white/30"
              }`}
            >
              <Heart className={`w-3 h-3 ${post.liked_by_me ? "fill-current" : ""}`} />
              {post.likes_count || 0}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold bg-white/15 text-white backdrop-blur-md">
              <MessageCircle className="w-3 h-3" />
              {post.comments_count || 0}
            </span>
            {post.remixes_count > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold bg-white/15 text-white backdrop-blur-md">
                <Repeat2 className="w-3 h-3" />
                {post.remixes_count}
              </span>
            )}
          </div>
        </div>

        {isVideo && (
          <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-ink-950/70 text-white text-[10px] font-bold tracking-wide backdrop-blur-md">
            VIDEO
          </span>
        )}
      </div>

      {post.caption && (
        <div className="px-4 py-3">
          <p className="text-sm text-ink-700 line-clamp-2 leading-snug">{post.caption}</p>
        </div>
      )}
    </button>
  );
};

export default CommunityCard;
